import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from "recharts";
import { format } from "date-fns";

const COLORS = ["#6c63ff", "#00d68f", "#ffd166", "#ff5c6c", "#a78bfa", "#34d399", "#f59e0b", "#ec4899", "#60a5fa", "#f97316"];

export default function StakeHistory() {
  const [transactions, setTransactions] = useState([]);
  const [partners, setPartners] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPartner, setFilterPartner] = useState("all");

  useEffect(() => {
    async function fetchData() {
      try {
        const [txSnap, partnerSnap] = await Promise.all([
          getDocs(query(collection(db, "transactions"), orderBy("date", "asc"))),
          getDocs(collection(db, "partners")),
        ]);

        const txList = txSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const partnerList = partnerSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setTransactions(txList);
        setPartners(partnerList);

        // Build cumulative chart data grouped by date
        const stakeMap = {};
        partnerList.forEach(p => stakeMap[p.email] = 0);

        const byDate = {};
        txList.forEach(tx => {
          const dateKey = tx.date?.seconds
            ? format(new Date(tx.date.seconds * 1000), "MMM d, yyyy")
            : tx.date;

          if (!byDate[dateKey]) byDate[dateKey] = { ...stakeMap };
          stakeMap[tx.partnerEmail] = (stakeMap[tx.partnerEmail] || 0) + tx.amount;
          byDate[dateKey] = { ...stakeMap, date: dateKey };
        });

        setChartData(Object.values(byDate));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const filteredTx = filterPartner === "all"
    ? transactions
    : transactions.filter(t => t.partnerEmail === filterPartner);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div style={styles.tooltip}>
          <div style={{ fontWeight: 700, marginBottom: "8px", color: "var(--text-primary)", fontSize: "13px" }}>{label}</div>
          {payload.map((p, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: "16px", fontSize: "13px", marginBottom: "4px" }}>
              <span style={{ color: p.color }}>{p.name}</span>
              <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>${p.value?.toLocaleString()}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "400px" }}>
      <div style={{ width: "36px", height: "36px", border: "3px solid var(--border)", borderTop: "3px solid var(--accent)", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
    </div>
  );

  return (
    <div style={styles.page} className="animate-in">
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Stake History</h1>
          <p style={styles.subtitle}>Track how partner ownership has changed over time</p>
        </div>
      </div>

      {/* Line chart */}
      <div className="card" style={{ marginBottom: "24px", padding: "24px" }}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>Cumulative Stakes Over Time</h2>
          <p style={styles.cardSub}>Dollar value invested per partner</p>
        </div>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fill: "var(--text-muted)", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fill: "var(--text-muted)", fontSize: 11 }}
                axisLine={false} tickLine={false}
                tickFormatter={v => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend formatter={v => <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>{v}</span>} />
              {partners.map((p, i) => (
                <Line
                  key={p.email}
                  type="monotone"
                  dataKey={p.email}
                  name={p.name}
                  stroke={COLORS[i % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 4, fill: COLORS[i % COLORS.length] }}
                  activeDot={{ r: 6 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={styles.empty}>No transaction history yet. Transactions will appear here after the first audit.</div>
        )}
      </div>

      {/* Transaction table */}
      <div className="card" style={{ padding: "24px" }}>
        <div style={{ ...styles.cardHeader, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h2 style={styles.cardTitle}>Transaction History</h2>
            <p style={styles.cardSub}>{filteredTx.length} transaction{filteredTx.length !== 1 ? "s" : ""}</p>
          </div>
          <select
            value={filterPartner}
            onChange={e => setFilterPartner(e.target.value)}
            className="input-field"
            style={{ width: "200px" }}
          >
            <option value="all">All Partners</option>
            {partners.map(p => (
              <option key={p.email} value={p.email}>{p.name}</option>
            ))}
          </select>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                {["Date", "Partner", "Type", "Amount", "Note", "Added By"].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredTx.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ ...styles.td, textAlign: "center", color: "var(--text-muted)", padding: "40px" }}>
                    No transactions found
                  </td>
                </tr>
              ) : (
                [...filteredTx]
                  .sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0))
                  .map((tx, i) => {
                    const isPositive = tx.amount >= 0;
                    const dateStr = tx.date?.seconds
                      ? format(new Date(tx.date.seconds * 1000), "MMM d, yyyy")
                      : tx.date || "—";
                    return (
                      <tr key={tx.id} style={{ ...styles.tr, background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                        <td style={styles.td}>{dateStr}</td>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{tx.partnerName}</td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.typeBadge,
                            background: isPositive ? "var(--green-dim)" : "var(--red-dim)",
                            color: isPositive ? "var(--green)" : "var(--red)",
                            borderColor: isPositive ? "rgba(0,214,143,0.2)" : "rgba(255,92,108,0.2)",
                          }}>
                            {tx.type || (isPositive ? "Contribution" : "Withdrawal")}
                          </span>
                        </td>
                        <td style={{ ...styles.td, fontWeight: 700, color: isPositive ? "var(--green)" : "var(--red)" }}>
                          {isPositive ? "+" : ""}${Math.abs(tx.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </td>
                        <td style={{ ...styles.td, color: "var(--text-secondary)", fontSize: "13px" }}>{tx.note || "—"}</td>
                        <td style={{ ...styles.td, color: "var(--text-muted)", fontSize: "13px" }}>{tx.addedBy || "—"}</td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { maxWidth: "1200px", margin: "0 auto" },
  header: { marginBottom: "28px" },
  title: { fontSize: "28px", fontFamily: "var(--font-display)", fontWeight: 800 },
  subtitle: { color: "var(--text-muted)", fontSize: "14px", marginTop: "4px" },
  cardHeader: { marginBottom: "20px" },
  cardTitle: { fontSize: "16px", fontFamily: "var(--font-display)", fontWeight: 700 },
  cardSub: { fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" },
  empty: { color: "var(--text-muted)", textAlign: "center", padding: "40px 0", fontSize: "14px" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left", padding: "10px 12px", fontSize: "11px",
    color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em",
    borderBottom: "1px solid var(--border)",
  },
  td: { padding: "13px 12px", fontSize: "14px", color: "var(--text-primary)", borderBottom: "1px solid rgba(255,255,255,0.03)" },
  tr: { transition: "var(--transition)" },
  typeBadge: {
    display: "inline-flex", alignItems: "center", padding: "3px 10px",
    borderRadius: "100px", fontSize: "11px", fontWeight: 600,
    border: "1px solid",
  },
  tooltip: {
    background: "var(--bg-elevated)", border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)", padding: "12px 16px", minWidth: "180px",
  },
};
